import React from 'react';
import Icon from '../../../components/AppIcon';

const SkillBreakdown = ({ progressData, stats }) => {
  // Calculate skill proficiency based on available data
  const calculateSkills = () => {
    const totalPlays = progressData?.progress?.length || 0;
    const completedPlays = progressData?.progress?.filter(p => p?.status === 'completed')?.length || 0;
    const avgCompletion = totalPlays > 0 ? (completedPlays / totalPlays) * 100 : 0;
    
    const quizAccuracy = stats?.avgQuizAccuracy || 0;
    const totalLinesRead = stats?.totalLinesRead || 0;

    return [
      {
        name: 'Reading Comprehension',
        icon: 'BookOpen',
        proficiency: Math.min(avgCompletion, 100),
        color: 'from-blue-500 to-cyan-600',
        barColor: 'bg-blue-500',
        description: 'Based on play completion rate'
      },
      {
        name: 'Quiz Performance',
        icon: 'Target',
        proficiency: quizAccuracy,
        color: 'from-green-500 to-emerald-600',
        barColor: 'bg-green-500',
        description: 'Based on quiz accuracy'
      },
      {
        name: 'Vocabulary Mastery',
        icon: 'BookText',
        proficiency: Math.min((totalLinesRead / 100) * 10, 100),
        color: 'from-purple-500 to-pink-600',
        barColor: 'bg-purple-500',
        description: 'Based on lines read and glossary usage'
      },
      {
        name: 'Story Understanding',
        icon: 'Brain',
        proficiency: Math.min((avgCompletion + quizAccuracy) / 2, 100),
        color: 'from-orange-500 to-red-600',
        barColor: 'bg-orange-500',
        description: 'Based on comprehension and completion'
      }
    ];
  };

  const skills = calculateSkills();

  const getProficiencyLevel = (proficiency) => {
    if (proficiency >= 80) return { label: 'Expert', color: 'text-green-400' };
    if (proficiency >= 60) return { label: 'Proficient', color: 'text-blue-400' };
    if (proficiency >= 40) return { label: 'Intermediate', color: 'text-yellow-400' };
    if (proficiency >= 20) return { label: 'Beginner', color: 'text-orange-400' };
    return { label: 'Starting', color: 'text-gray-400' };
  };

  return (
    <div className="bg-surface-lighter border border-border rounded-xl p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-1">Skill Breakdown</h2>
        <p className="text-text-secondary text-sm">
          Track your proficiency across key learning areas
        </p>
      </div>

      <div className="space-y-6">
        {skills?.map((skill, index) => {
          const level = getProficiencyLevel(skill?.proficiency);
          
          return (
            <div key={index} className="group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${skill?.color} bg-opacity-20 group-hover:scale-110 transition-transform`}>
                    <Icon name={skill?.icon} size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{skill?.name}</h3>
                    <p className="text-text-secondary text-xs">{skill?.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${level?.color}`}>
                    {level?.label}
                  </p>
                  <p className="text-2xl font-bold text-white">
                    {Math.round(skill?.proficiency)}%
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-surface-dark rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full ${skill?.barColor} transition-all duration-500 rounded-full`}
                  style={{ width: `${skill?.proficiency}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Improvement Suggestions */}
      <div className="mt-6 p-4 bg-surface-dark rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} className="text-accent-purple flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-white font-semibold mb-2">Improvement Suggestions</h4>
            <ul className="space-y-1 text-text-secondary text-sm">
              {stats?.avgQuizAccuracy < 70 && (
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple">•</span>
                  <span>Review glossary terms before taking quizzes to improve comprehension</span>
                </li>
              )}
              {stats?.playsCompleted < 3 && (
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple">•</span>
                  <span>Complete more plays to enhance your reading proficiency</span>
                </li>
              )}
              {stats?.consecutiveDays < 5 && (
                <li className="flex items-start gap-2">
                  <span className="text-accent-purple">•</span>
                  <span>Build a daily reading habit to maintain your learning streak</span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillBreakdown;